// Actions

// Hooks
import { useOrganization } from './use-organization';

// Selectors

// Types
import {
  ModuleAccessConfiguration,
  ModuleAccessLevel,
  ModuleName,
  AccountClassificationType,
  ModuleAccessLevelString,
} from 'Shared/types/SoftwareModuleAccessConfig.type';

export const useSmac = () => {
  const legacyModules = [ModuleName.Hire, ModuleName.Design, ModuleName.Inspire, ModuleName.Diagnose];
  const pi2Modules = [ModuleName.HireHero, ModuleName.Design2, ModuleName.Inspire2, ModuleName.Diagnose2];

  const { isPiOrganization, organization, isPiDemoAccount } = useOrganization();

  const smac: ModuleAccessConfiguration[] =
    organization?.software_module_access_config?.softwareModuleAccessConfig || [];

  const accountClassificationType: AccountClassificationType =
    organization?.software_module_access_config?.accountClassification?.classificationType ||
    AccountClassificationType.UNKNOWN;

  const isPartnerAccount = [
    AccountClassificationType.SELLER,
    AccountClassificationType.SELLER_GRANTED_TRIAL,
    AccountClassificationType.WORKSHOP,
    AccountClassificationType.DYNAMIC_DEMO,
    AccountClassificationType.TEST,
    AccountClassificationType.LEGACY_BUSINESS_ACCOUNT,
    AccountClassificationType.LEGACY_DEMO_ACCOUNT,
  ].includes(accountClassificationType);

  const hasAccess = (module: ModuleAccessConfiguration) => {
    return (
      (typeof module.accessLevel === 'number' && module.accessLevel > ModuleAccessLevel.NoAccess) ||
      (typeof module.accessLevel === 'string' &&
        [ModuleAccessLevelString.BasicAccess, ModuleAccessLevelString.CommonAccess].includes(module.accessLevel))
    );
  };

  const hasBasicAccess = (module: ModuleAccessConfiguration) => {
    return (
      (typeof module.accessLevel === 'number' && module.accessLevel === ModuleAccessLevel.BasicAccess) ||
      (typeof module.accessLevel === 'string' && module.accessLevel === ModuleAccessLevelString.BasicAccess)
    );
  };

  const hasAccessToNonPerformModules = smac.some(module => hasAccess(module) && module.name !== ModuleName.Perform);

  const hasClassicAccess = smac.some(module => hasAccess(module) && legacyModules.includes(module.name));

  const hasPi2Access = smac.some(module => hasAccess(module) && pi2Modules.includes(module.name));

  const isStandAlone = isPiOrganization && !hasClassicAccess && !hasPi2Access;

  const hasInspireAccess = !!smac.find(
    module => hasAccess(module) && (module.name === ModuleName.Inspire || module.name === ModuleName.Inspire2)
  );

  const hasDesign2Access = isPiDemoAccount
    ? true
    : !!smac.find(module => hasAccess(module) && module.name === ModuleName.Design2);

  // Perform Essentials - Talent Optimization Bundle, Excludes Goals, Reviews and Feedback
  const isToeBundle = smac.some(module => module.name === ModuleName.Perform && hasBasicAccess(module));

  return {
    smac,
    isPartnerAccount,
    accountClassificationType,
    hasAccessToNonPerformModules,
    hasClassicAccess,
    hasPi2Access,
    hasInspireAccess,
    isStandAlone,
    isToeBundle,
    hasDesign2Access,
  };
};
